import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import { FormControl, InputLabel } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Divider from '@material-ui/core/Divider'
import { DatePicker } from '@material-ui/pickers'
import { MultiSelect } from 'react-multi-select-component'
import moment, { Moment } from 'moment'

import { Dialog, UploadButton, snackbar, Notice } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { STRINGS } from 'consts'
import { PrizeCategory } from 'consts/prize'
import { resizeFile } from 'utils'

import { ManualReward, CreateManualRewardPayload, CreateManualRewardResponse } from '../../types'
import { Tag } from '../../../Tags/types'
import { createManualReward } from '../../api'
import { useStyles } from './styles'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 25,
      message: 'No puede superar los 25 caracteres',
    },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.length <= 22,
      message: 'No puede superar los 22 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  tagsSelected: [],
  icon: [],
  expiration: [],
  pushTitle: [
    {
      validate: (title?: string) => (title ? title.length <= 50 : true),
      message: 'No puede superar los 50 caracteres',
    },
  ],
  pushDescription: [
    {
      validate: (description?: string) => (description ? description.length <= 192 : true),
      message: 'No puede superar los 192 caracteres',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: any) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID Beneficio Toteat es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: any) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface CreateManualRewardDialogProps {
  handleClose: () => void
  onDone: (newManualReward: ManualReward) => void
  tags: Tag[]
  calcUsersByTags: (tagsSelected: any[], allTagsAreRequired?: boolean) => Promise<number>
}

const CreateManualRewardDialog: React.FC<CreateManualRewardDialogProps> = ({
  handleClose,
  onDone,
  tags,
  calcUsersByTags,
}) => {
  const classes = useStyles()

  const [toteatBenefitId] = useState<string>('')
  const [skusList] = useState<string[]>([])
  const [storesEnabled] = useState(true)
  const [tucanEnabled] = useState(false)
  const [toteatEnabled] = useState(false)
  const [category] = useState<PrizeCategory | undefined>()
  const [title, setTitle] = useState('')
  const [shortTitle, setShortTitle] = useState('')
  const [icon, setIcon] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState('')
  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState('')
  const [pushMessage, setPushMessage] = useState(false)
  const [pushTitle, setPushTitle] = useState('')
  const [pushDescription, setPushDescription] = useState('')
  const [active, setActive] = useState(false)
  const [tagsSelected, setTagsSelected] = useState<any[]>([])
  const [usersByTags, setUsersByTags] = useState(0)
  const [expiration, setExpiration] = useState<Moment | null>(null)
  const [additionalText, setAdditionalText] = useState('')
  const firstExpirationDate = moment().format('YYYY-MM-DD hh:mm')
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(false)

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const [createManualRewardApi, isLoading] = useApiCall<
    CreateManualRewardPayload,
    CreateManualRewardResponse
  >(createManualReward)

  useEffect(() => {
    const getUsersByTags = async () => {
      return await calcUsersByTags(tags, false)
    }
    getUsersByTags().then(el => {
      setUsersByTags(el)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags])

  useEffect(() => {
    calcUsersByTags(tagsSelected, allTagsAreRequired).then(el => setUsersByTags(el))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTagsAreRequired, tagsSelected])

  const handleSetTags = async (e: any[]) => {
    setTagsSelected(e)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = async e => {
    const total = await calcUsersByTags(tagsSelected, !allTagsAreRequired)
    await setAllTagsAreRequired(!allTagsAreRequired)
    setUsersByTags(total)
  }

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setTitle(e.target.value)
  }

  const handleShortTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setShortTitle(e.target.value)
  }

  const handleIconChange = async (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    const resizedIcon = await resizeFile(file, 192, 192)
    setIcon(resizedIcon)
  }

  const handleImageChange = async (file: File) => {
    setImagePreview(URL.createObjectURL(file))
    const resizedImage = await resizeFile(file, 192, 192)
    setImage(resizedImage)
  }

  const handleChangePushMessage: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPushMessage(!pushMessage)
    if (!pushMessage) {
      setPushTitle('')
      setPushDescription('')
    }
  }

  const handleChangeActive: React.ChangeEventHandler<HTMLInputElement> = e => {
    setActive(!active)
  }

  const handleChangePushTitle: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPushTitle(e.target.value)
  }

  const handleChangepushDescription: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPushDescription(e.target.value)
  }

  const handleDateChange = (newDate: Moment | null) => {
    setExpiration(newDate)
  }

  const handleAdditionalTextChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAdditionalText(e.target.value)
  }

  const getRequiredFields = () => {
    const baseRequiredFields = ['title', 'shortTitle', 'additionalText']
    const pushTitleRequiredFields = ['pushTitle', 'pushDescription']

    if (pushMessage) {
      baseRequiredFields.push(...pushTitleRequiredFields)
    }

    if (toteatEnabled) {
      if (category === PrizeCategory.General) {
        baseRequiredFields.push('skusList')
      } else if (category === PrizeCategory.Checkout) {
        baseRequiredFields.push('toteatBenefitId')
      }
    }

    return baseRequiredFields
  }

  const hasPushNotificaitonAndWasntCompleted =
    pushMessage && (!image || !pushDescription || !pushTitle)

  const fields = {
    title,
    shortTitle,
    icon,
    tagsSelected,
    additionalText,
    expiration,
    pushMessage,
    pushTitle,
    pushDescription,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    category,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled =
    hasErrors || isAnyFieldEmpty || isLoading || hasPushNotificaitonAndWasntCompleted

  const onCreateManualReward = async () => {
    if (!icon || !expiration) return
    if (pushMessage && (!image || !pushDescription || !pushTitle)) {
      snackbar.show('Todos los campos de la push son requeridos.')
      return
    }
    //! tagsSelected.length
    const manualReward = {
      title,
      shortTitle,
      icon,
      image,
      pushMessage,
      pushTitle,
      pushDescription,
      active,
      tagsSelected,
      expiration: expiration.toISOString(),
      additionalText,
      allTagsAreRequired,
      category,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
    }
    try {
      const { manualReward: newManualReward } = await createManualRewardApi(manualReward)
      onDone(newManualReward)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo crear premio. Intente de nuevo.')
    }
  }

  return (
    <Dialog
      title="Nuevo reward manual"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateManualReward}
    >
      <Grid container spacing={3}>
        <Grid item xs={11}>
          <TextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.title.hasError}
            helperText={errors.title.message}
            inputProps={{ maxLength: 25 }}
          />
        </Grid>
        <Grid item xs={11}>
          <TextField
            label="Descripcion"
            value={shortTitle}
            fullWidth
            onChange={handleShortTitleChange}
            disabled={isLoading}
            required
            error={errors.shortTitle.hasError}
            helperText={errors.shortTitle.message}
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto adicional"
            value={additionalText}
            fullWidth
            onChange={handleAdditionalTextChange}
            disabled={isLoading}
            required
            error={errors.additionalText.hasError}
            helperText={errors.additionalText.message}
          />
        </Grid>
        <Grid direction="row" xs={12} justify="space-between" className={classes.imageContainer}>
          <Grid item xs={6}>
            <div className={classes.iconUploadContainer}>
              <UploadButton
                id="manualReward-icon-upload-button"
                accept=".jpg, .jpeg, .png"
                label="Subir ícono"
                onChange={handleIconChange}
                maxSizeInMB={2}
              />
              <div className={classes.iconUploadPreviewContainer}>
                <img src={iconPreview} alt="" className={classes.iconPreview} />
              </div>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={classes.iconUploadContainer}>
              <UploadButton
                id="manualReward-image-upload-button"
                accept=".jpg, .jpeg, .png"
                label="Subir imagen popup"
                onChange={handleImageChange}
                maxSizeInMB={2}
              />
              <div className={classes.iconUploadPreviewContainer}>
                <img src={imagePreview} alt="" className={classes.iconPreview} />
              </div>
            </div>
          </Grid>
        </Grid>
        <Grid item xs={11}>
          <InputLabel className={classes.multiselectTitle}>Tag</InputLabel>
          <FormControl className={classes.fullWidth}>
            <MultiSelect
              options={tagsForSelect}
              value={tagsSelected}
              onChange={handleSetTags}
              ClearIcon
              labelledBy="Seleccionar Tags"
              hasSelectAll={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={11}>
          <FormControlLabel
            control={
              <Checkbox onChange={handleAllTagsAreRequiredChange} checked={allTagsAreRequired} />
            }
            label="Sólo usuarios con todos los tags seleccionados"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            value={expiration}
            onChange={handleDateChange}
            autoOk
            disablePast
            format={STRINGS.DATE_FORMAT}
            minDate={firstExpirationDate}
            initialFocusedDate={firstExpirationDate}
            disabled={isLoading}
            style={{ width: '100%' }}
            label="Vencimiento"
            required
          />
        </Grid>
        <Grid item xs={11}>
          <FormControlLabel
            control={<Checkbox onChange={handleChangePushMessage} checked={pushMessage} />}
            label="Sale con push?"
          />
        </Grid>
        {pushMessage && (
          <>
            <Grid item xs={11}>
              <TextField
                label="Titulo push"
                value={pushTitle}
                fullWidth
                required
                error={errors.pushTitle.hasError}
                helperText={errors.pushTitle.message}
                onChange={handleChangePushTitle}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={11}>
              <TextField
                label="Descripción push"
                value={pushDescription}
                fullWidth
                required
                error={errors.pushDescription.hasError}
                helperText={errors.pushDescription.message}
                onChange={handleChangepushDescription}
                disabled={isLoading}
              />
            </Grid>
          </>
        )}
        <Grid item xs={11}>
          <FormControlLabel
            control={<Checkbox onChange={handleChangeActive} checked={active} />}
            label="Activar Reward"
          />
        </Grid>
        <Grid item xs={11}>
          <Divider variant="middle" />
        </Grid>
        <Grid item xs={11}>
          <Notice>Esta acción impactará a {usersByTags} usuarios</Notice>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { CreateManualRewardDialog }
